import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import { formatStamp, formatStampShort } from '../../utils/datetime';
import { health, HealthDelta, Tip } from '../risk/riskUi';
import { Modal } from '../review/reviewUi';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';
import '../../styles/TraceDashboard.css';
import '../../styles/Dhf.css';

const BAD = { background: '#FBE0D5', color: '#8A2A0B' };
const WARN = { background: '#FFF1D6', color: '#7A4B00' };
const GOOD = { background: '#DDF3E4', color: '#17603A' };
const INFO = { background: '#E3ECFB', color: '#1F4E9E' };
const GREY = { background: '#F3F0F8', color: '#4A4560' };
const NEUTRAL = { background: '#EAE5F5', color: '#3D2070' };

// Same order as the server's BL_BUCKETS: worst first.
const BUCKETS = [
  ['newly_failed', 'Newly failed', BAD],
  ['lost_coverage', 'Lost coverage', WARN],
  ['left_document', 'Out of document', WARN],
  ['removed', 'Removed', GREY],
  ['reopened', 'No longer complete', WARN],
  ['moved', 'Moved', NEUTRAL],
  ['placed', 'Placed', INFO],
  ['added', 'Added', GOOD],
  ['fixed', 'Test fixed', INFO],
  ['newly_covered', 'Newly covered', GOOD],
  ['completed', 'Completed', GOOD],
  ['status_changed', 'Status changed', NEUTRAL],
  ['tests_changed', 'Tests changed', NEUTRAL],
];
const BUCKET_STYLE = Object.fromEntries(BUCKETS.map(([k, , st]) => [k, st]));
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map(([k, l]) => [k, l]));

const pair = ([a, b]) => (a === b ? `${a ?? '—'}` : `${a ?? '—'} → ${b ?? '—'}`);

function TakeModal({ setupId, builtAt, onClose, onTaken }) {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!name.trim()) { setError('Give the baseline a name, e.g. “Design review 2”.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await dhfApi.takeBaseline(setupId, { name: name.trim(), note: note.trim() });
      onTaken(r.baseline);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Take baseline"
      subtitle={`A read-only snapshot of the latest DHF build${builtAt ? ` (${formatStampShort(builtAt)})` : ''}: every document and its health, and every item's place, status, coverage and test results. Refresh first if you need the very latest from Orcanos.`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Take baseline'}
          </button>
        </>
      )}
    >
      <label className="drv-field">
        <span className="drv-lbl">Name</span>
        <input className="drv-input" value={name} onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="e.g. Design review 2 · Submission" maxLength={120} autoFocus />
      </label>
      <label className="drv-field">
        <span className="drv-lbl">Note <span>— optional</span></span>
        <textarea className="drv-input rsk-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. DHF as sent for design review DR-3" maxLength={1000} />
      </label>
      <p className="drv-small" style={{ margin: 0 }}>
        Baselines can be archived but never edited or deleted. Each one carries a SHA-256 of its content.
      </p>
      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}

/**
 * DHF baselines & compare (2026-10-27) — the Risk / Traceability baselines screen for a DHF.
 * A baseline freezes the latest DHF build (its build stamp is shown); compare is per document
 * (health, items, paragraphs) and per item by level + id, one bucket per item; the AI analysis
 * is saved per exact change.
 */
export default function DhfBaselinesPage({ aiEnabled, canManagePanels }) {
  const { setupId } = useParams();
  const [panel, setPanel] = useState(null);
  const [builtAt, setBuiltAt] = useState(null);
  const [list, setList] = useState(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('now');
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [taking, setTaking] = useState(false);
  const [allDocs, setAllDocs] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, bl] = await Promise.all([dhfApi.get(setupId), dhfApi.baselines(setupId)]);
      setPanel(d.panel || null);
      setBuiltAt(d.built ? d.built_at : null);
      setList(bl.baselines || []);
      const live = (bl.baselines || []).filter((x) => !x.archived_at);
      setA((cur) => cur || (live[0]?.id || ''));
    } catch (e) {
      setError(e.message);
      setList((x) => x || []);
    }
  }, [setupId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!a) { setCmp(null); return undefined; }
    let live = true;
    setLoading(true);
    setBucket('');
    dhfApi.compare(setupId, a, b)
      .then((d) => { if (live) { setCmp(d); setAnalysis(d.analysis || null); setError(''); } })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [setupId, a, b]);

  const analyse = async (force = false) => {
    setAnalysing(true);
    try {
      const r = await dhfApi.analyse(setupId, { a, b, force });
      setAnalysis(r.analysis);
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalysing(false);
    }
  };

  const archive = async (x) => {
    try {
      await dhfApi.archiveBaseline(setupId, x.id, !x.archived_at);
      load();
    } catch (e) { setError(e.message); }
  };

  const live = (list || []).filter((x) => !x.archived_at);
  const changes = (cmp?.diff?.changes || []).filter((c) => !bucket || c.bucket === bucket);
  const docRows = (cmp?.diff?.docs || []).filter((d) => allDocs || d.changed);
  const nDocsChanged = (cmp?.diff?.docs || []).filter((d) => d.changed).length;
  const sa = cmp?.score_a?.health;
  const sb = cmp?.score_b?.health;

  return (
    <div className="drv-page rsk-page">
      <div className="rsk-crumbs">
        <Link to="/dhf">Design History File</Link><span>/</span>
        <Link to={`/dhf/${setupId}`}>{panel?.name || 'DHF'}</Link><span>/</span>
        <strong>Baselines</strong>
      </div>
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Baselines</h1>
          <p className="drv-sub">Read-only snapshots of the DHF — compare any two, or a baseline with the latest build.</p>
        </div>
        {canManagePanels && (
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => setTaking(true)} disabled={!builtAt}
            title={builtAt ? undefined : 'Build the DHF first'}>Take baseline</button>
        )}
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}

      <div className="rsk-two">
        <aside className="rsk-bl-list" aria-label="Baselines">
          <div className="rsk-lbl">Baselines · {live.length}</div>
          {list === null && <div className="drv-muted">Loading…</div>}
          {list?.length === 0 && (
            <div className="drv-empty" style={{ padding: 18 }}>
              No baselines yet. Take one before a design review or a submission — then every later change to the
              documents can be compared against it.
            </div>
          )}
          {(list || []).map((x) => (
            <div key={x.id} className={`rsk-bl${x.archived_at ? ' rsk-bl--arch' : ''}`}>
              <div className="rsk-row-between">
                <strong>{x.name}</strong>
                <span className="rsk-bl-score">{health(x.score) ?? '—'}</span>
              </div>
              <div className="rsk-fine">{formatStamp(x.created_at, { dateOnly: true })} · {x.created_by_name} · {x.n_docs} documents · {x.n_items} items</div>
              <Tip as="div" className="rsk-sha" heading="SHA-256 fingerprint"
                tip={<span className="rsk-tip-mono">{x.sha256}</span>}>SHA-256 {x.sha256?.slice(0, 4)}…{x.sha256?.slice(-4)}</Tip>
              {x.note && <div className="rsk-fine">{x.note}</div>}
              {canManagePanels && (
                <button type="button" className="drv-link rsk-small-link" style={{ marginLeft: 0, alignSelf: 'flex-start' }}
                  onClick={() => archive(x)}>{x.archived_at ? 'Restore' : 'Archive'}</button>
              )}
            </div>
          ))}
          <div className="rsk-fine">
            A baseline keeps every document with its health, and every item with its document, paragraph, status,
            coverage and test results, as of the build it was taken from. It can be archived but never edited or deleted.
          </div>
        </aside>

        <section className="rsk-cmp" aria-label="Compare">
          {live.length > 0 && (
            <div className="rsk-card rsk-pad rsk-cmp-bar">
              <label className="drv-field">
                <span className="drv-lbl">From</span>
                <select className="drv-select" value={a} onChange={(e) => setA(e.target.value)}>
                  {live.map((x) => <option key={x.id} value={x.id}>{x.name} — {formatStamp(x.created_at, { dateOnly: true })}</option>)}
                </select>
              </label>
              <span className="rsk-arr" aria-hidden="true">→</span>
              <label className="drv-field">
                <span className="drv-lbl">To</span>
                <select className="drv-select" value={b} onChange={(e) => setB(e.target.value)}>
                  <option value="now">Latest build{builtAt ? ` (${formatStampShort(builtAt)})` : ''}</option>
                  {live.filter((x) => x.id !== a).map((x) => <option key={x.id} value={x.id}>{x.name} — {formatStamp(x.created_at, { dateOnly: true })}</option>)}
                </select>
              </label>
            </div>
          )}

          {loading && <div className="drv-muted">Comparing…</div>}

          {cmp && !loading && (
            <>
              <div className="rsk-tiles">
                <div className="rsk-tile rsk-tile--static">
                  <span className="rsk-tile-name">DHF health</span>
                  <span className="rsk-tile-n">{sa ?? '—'} → {sb ?? '—'}
                    {sa != null && sb != null && sa !== sb && <HealthDelta from={sa} to={sb} />}
                  </span>
                  <span className="rsk-tile-hint">{cmp.diff.n_a} → {cmp.diff.n_b} items</span>
                </div>
                <button type="button" className={`rsk-tile${!bucket ? ' rsk-tile--on' : ''}`} onClick={() => setBucket('')}>
                  <span className="rsk-tile-name">All changes</span>
                  <span className="rsk-tile-n">{cmp.diff.changes.length}</span>
                  <span className="rsk-tile-hint">{cmp.diff.unchanged} unchanged</span>
                </button>
                {BUCKETS.filter(([k]) => cmp.diff.counts[k]).map(([k, label]) => (
                  <button key={k} type="button" className={`rsk-tile${bucket === k ? ' rsk-tile--on' : ''}`}
                    onClick={() => setBucket(bucket === k ? '' : k)}>
                    <span className="rsk-tile-name">{label}</span>
                    <span className="rsk-tile-n">{cmp.diff.counts[k]}</span>
                  </button>
                ))}
              </div>

              <div className="rsk-card rsk-pad rsk-ai">
                <div className="rsk-row-between">
                  <div className="rsk-lbl">AI analysis of this change</div>
                  {analysis && (
                    <span className="rsk-fine">
                      {analysis.created_at ? `${formatStamp(analysis.created_at)} · ` : ''}
                      {aiEnabled && <button type="button" className="drv-link rsk-small-link" onClick={() => analyse(true)} disabled={analysing}>Regenerate</button>}
                    </span>
                  )}
                </div>
                {!analysis && (
                  aiEnabled ? (
                    <button type="button" className="drv-btn" style={{ alignSelf: 'flex-start' }} onClick={() => analyse(false)} disabled={analysing}>
                      {analysing ? 'Analysing…' : 'Analyse with AI'}
                    </button>
                  ) : <div className="rsk-fine">AI is not enabled for this account.</div>
                )}
                {analysis && (
                  <>
                    <p className="rsk-ai-text">{analysis.summary}</p>
                    {analysis.worth_a_look?.length > 0 && <div className="rsk-lbl">Worth a look</div>}
                    {(analysis.worth_a_look || []).map((w) => (
                      <div className="rsk-look" key={w.text}>
                        <span className="rsk-pill" style={WARN}>{w.kind}</span>
                        <span className="rsk-look-text">{w.text}</span>
                        <span className="rsk-look-keys">{(w.keys || []).map((k) => <span key={k}>{k}</span>)}</span>
                      </div>
                    ))}
                    <div className="rsk-fine">AI suggestions — check them against the documents before acting.</div>
                  </>
                )}
              </div>

              <div className="rsk-card">
                <div className="rsk-pad rsk-sub rsk-row-between" style={{ borderBottom: '1px solid #EFECF6' }}>
                  <span>
                    <strong style={{ color: '#1A1631' }}>Documents</strong>
                    {' '}· {nDocsChanged} changed of {cmp.diff.docs.length}
                  </span>
                  <button type="button" className="drv-link rsk-small-link" onClick={() => setAllDocs(!allDocs)}>
                    {allDocs ? 'Only changed' : 'Show all'}
                  </button>
                </div>
                <div className="rsk-table-wrap">
                  <table className="rsk-table" style={{ minWidth: 760 }}>
                    <thead><tr><th>Document</th><th>Health</th><th>Items</th><th>Paragraphs</th><th>Empty paragraphs</th><th>Items outside a paragraph</th></tr></thead>
                    <tbody>
                      {docRows.map((d) => {
                        const ha = health(d.score_a);
                        const hb = health(d.score_b);
                        return (
                          <tr key={d.id}>
                            <td>
                              <span className="rsk-key">{d.key}</span>
                              {d.state !== 'same' && (
                                <span className="rsk-pill" style={{ ...(d.state === 'added' ? GOOD : GREY), marginLeft: 6 }}>
                                  {d.state === 'added' ? 'New' : 'Gone'}
                                </span>
                              )}
                              <div className="rsk-name">{d.name}</div>
                            </td>
                            <td className="rsk-sub">
                              {ha ?? '—'}{ha !== hb && <> → <b>{hb ?? '—'}</b></>}
                              {ha != null && hb != null && ha !== hb && <HealthDelta from={ha} to={hb} />}
                            </td>
                            <td className="rsk-sub">{pair(d.n_items)}</td>
                            <td className="rsk-sub">{pair(d.paragraphs)}</td>
                            <td className="rsk-sub">{pair(d.empty_paragraphs)}</td>
                            <td className="rsk-sub">{pair(d.loose_items)}</td>
                          </tr>
                        );
                      })}
                      {docRows.length === 0 && (
                        <tr><td colSpan={6} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No document changed.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rsk-card">
                <div className="rsk-pad rsk-sub" style={{ borderBottom: '1px solid #EFECF6' }}>
                  <strong style={{ color: '#1A1631' }}>{bucket ? `${changes.length} ${BUCKET_LABEL[bucket].toLowerCase()}` : `${cmp.diff.changes.length} changed · ${cmp.diff.unchanged} unchanged`}</strong>
                  {' '}· items matched by level and id
                </div>
                <div className="rsk-table-wrap">
                  <table className="rsk-table" style={{ minWidth: 900 }}>
                    <thead><tr><th>Item</th><th>Change</th><th>Was in</th><th>Now in</th><th>Detail</th></tr></thead>
                    <tbody>
                      {changes.map((c) => (
                        <tr key={`${c.bucket}-${c.uid}`}>
                          <td><span className="rsk-key">{c.key}</span><div className="rsk-name">{c.name}</div><div className="rsk-fine">{c.level}</div></td>
                          <td><span className="rsk-pill" style={BUCKET_STYLE[c.bucket]}>{BUCKET_LABEL[c.bucket]}</span></td>
                          <td className="rsk-sub">{c.before}</td>
                          <td className="rsk-sub">{c.after}</td>
                          <td className="rsk-sub">{(c.details || []).map((d) => <div key={d}>{d}</div>)}</td>
                        </tr>
                      ))}
                      {changes.length === 0 && (
                        <tr><td colSpan={5} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No changes.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {taking && (
        <TakeModal setupId={setupId} builtAt={builtAt} onClose={() => setTaking(false)}
          onTaken={(bl) => { setTaking(false); setA(bl.id); load(); }} />
      )}
    </div>
  );
}
